import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CustomControl } from "./CustomControl";

/**
 * スマホ・タブレット幅向けの出典（attribution）表示コントロール
 *
 * スマホ幅（sm 未満）では Leaflet 標準の出典欄が左下のスケール・中心座標コントロールと
 * 重なるため、標準の出典欄は CSS（leaflet-attribution.css）で隠し、代わりに情報アイコンの
 * ボタンを置く。ボタンを押すとダイアログで出典の全文を表示する。
 *
 * タブレット幅（sm 以上 md 未満）では標準の出典欄は出すが、写真レイヤーを重ねると長くなり
 * CSS で 1 行に省略される。省略の全文はホバーでしか開けずタッチ端末では読めないため、
 * レイヤー選択パネルの閉じるボタンと同じ md 未満までこのボタンを出す。
 *
 * 出典の文字列は各レイヤーが Leaflet の attributionControl に登録したものを
 * そのまま使うため、ダイアログを開くたびに標準コントロールの HTML を読み出す。
 * （標準コントロールは display:none でも DOM に残り、内容は更新され続ける）
 *
 * PC 幅では何も描画しない（標準の出典欄をそのまま使う）。
 */
export const MapAttributionControl: React.FC = () => {
  const map = useMap();
  const theme = useTheme();
  // ホバーで省略表示を開けない幅（スマホ・タブレット）では、ダイアログで全文を読めるようにする
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [attributionHtml, setAttributionHtml] = useState<string | null>(null);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      setAttributionHtml(
        map.attributionControl?.getContainer()?.innerHTML ?? ""
      );
    },
    [map]
  );

  const handleClose = useCallback((): void => {
    setAttributionHtml(null);
  }, []);

  if (!isNarrowScreen) {
    return null;
  }

  return (
    <CustomControl position="bottomright">
      <Tooltip title="出典">
        <IconButton
          size="small"
          aria-label="出典を表示"
          onClick={handleOpen}
          onDoubleClick={(event) => {
            event.stopPropagation();
          }}
          sx={{
            width: 24,
            height: 24,
            background: "rgba(255,255,255,0.9)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
            color: "#4b5563",
            "&:hover": { background: "#fff" },
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: "16px" }} />
        </IconButton>
      </Tooltip>
      <Dialog
        open={attributionHtml !== null}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        aria-labelledby="map-attribution-dialog-title"
      >
        <DialogTitle id="map-attribution-dialog-title">出典</DialogTitle>
        <DialogContent dividers>
          <AttributionHtml html={attributionHtml ?? ""} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </CustomControl>
  );
};

/**
 * Leaflet の出典欄 HTML をそのまま表示する。
 * リンクはダイアログから地図画面を離れずに開けるよう、別タブで開くようにする。
 * HTML は各レイヤー実装が定義した出典文字列（アプリ内の定数）のみで、外部入力は含まない。
 */
const AttributionHtml: React.FC<{ html: string }> = ({ html }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.querySelectorAll("a").forEach((anchor) => {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    });
  }, [html]);

  return (
    <div
      ref={ref}
      style={{ fontSize: 13, lineHeight: 1.7, wordBreak: "break-word" }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
